import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Layout,
  SupportIcon,
} from "lola-framework-ui-test";
import { BodyCopy } from "../components/BodyCopy";
import { AuraLayout } from "../components/AuraLayout";
import { TransactionsHistory } from "../components/TransactionsHistory";
import { APP_ROUTES } from "../constants";
import { useStore } from "../store/globalState";
import { useSessionStore } from "../store/useSessionStore";
import { useRequestStore } from "../store/useRequestStore";
import { useExitToPartnerOrLanding } from "../hooks/useExitToPartnerOrLanding";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { usePullToRefresh } from "../hooks/usePullToRefresh";
import { listOriginations } from "../api/deferred-originations";
import type { OriginationSummary } from "../api/deferred-originations";
import {
  upsertOriginationInList,
  useOriginationSSE,
  hasNonTerminalOriginations,
} from "../hooks/useOriginationSSE";
import { useSenderOperationRefresh } from "../hooks/useSenderOperationRefresh";
import { useLaunchCashDepositPayment } from "../hooks/useLaunchCashDepositPayment";
import { isUnfundedCashDeposit } from "../api/cash-deposit";
import { isOriginationComplete } from "../utils/bindingStatus";
import { amountFormatter, getCurrentDateFormatted } from "../utils";
import type { ITransaction } from "../interfaces/transactions";
import { getEvents, safeCapture } from "../analytics";
import { useSupportSheet } from "../hooks/useSupportSheet";
import { NotificationsBell } from "../components/notifications/NotificationsBell";
import { useNovuInbox } from "../components/notifications/NovuInboxProvider";
import { Navbar } from "../components/Navbar";

const PULL_THRESHOLD = 70;

const formatDate = (isoDate?: string): string => {
  if (!isoDate) return getCurrentDateFormatted();
  const date = new Date(isoDate);
  if (isNaN(date.getTime())) return getCurrentDateFormatted();
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");
  const year = date.getFullYear().toString().slice(-2);
  return `${day}/${month}/${year}`;
};

const recipientLabel = (hint: OriginationSummary["recipientHint"]): string =>
  [hint.firstName, hint.lastName].filter(Boolean).join(" ").trim();

/**
 * Send hub: lists deferred payout originations the sender created from this link.
 */
export const HomePage = memo(() => {
  const { t } = useTranslation();
  const { theme, setAmountState, setPaymentState, setFundingInstrument } =
    useStore();
  const { session } = useSessionStore();
  const { reset: resetComposeDraft } = useRequestStore();
  const { redirectTo, linkId: linkIdFromRoute } = useRouteWithToken();
  const { exitToPartnerOrLanding, canExitToPartner } =
    useExitToPartnerOrLanding();

  useEffect(() => {
    safeCapture(getEvents().SCREEN_VIEW("home"));
  }, []);

  const userStatus = session.data?.userStatus;
  const canOriginate = !userStatus?.blocked && !userStatus?.suspended;

  const startNewTransfer = useCallback(() => {
    if (!canOriginate) return;
    // Send parity: clear persisted corridor/funding draft so TransferPage
    // re-seeds from corridors instead of restoring a previous MX (etc.).
    resetComposeDraft();
    setAmountState({ amount: "", anchor: "send" });
    setPaymentState({ payment: null });
    setFundingInstrument(null);
    redirectTo(APP_ROUTES.TRANSFER_PAGE);
  }, [
    canOriginate,
    resetComposeDraft,
    setAmountState,
    setPaymentState,
    setFundingInstrument,
    redirectTo,
  ]);

  const colorConfig = theme.whiteView;
  const { backgroundBtn, textColorBtn, dropzoneColors, bodyCopy } = colorConfig;

  const linkId = linkIdFromRoute || session.data?.linkId;

  const [originations, setOriginations] = useState<OriginationSummary[]>([]);
  const [payingOperationId, setPayingOperationId] = useState<string | null>(
    null
  );
  const containerRef = useRef<HTMLDivElement | null>(null);
  const loadingRef = useRef(false);

  const {
    launch: launchCashDeposit,
    launching: launchingCashDeposit,
  } = useLaunchCashDepositPayment(linkId);

  const handlePayAtStore = useCallback(
    async (operationId: string) => {
      setPayingOperationId(operationId);
      try {
        await launchCashDeposit(operationId);
      } finally {
        setPayingOperationId(null);
      }
    },
    [launchCashDeposit]
  );

  const mapOrigination = useCallback(
    (item: OriginationSummary): ITransaction => {
      const canPay =
        Boolean(item.operationId) &&
        isUnfundedCashDeposit(item.fundingMethod, item.fundingStatus);

      return {
        originationId: item.originationId,
        operationId: item.operationId ?? undefined,
        status: item.bindingState,
        operationStatus: item.operationStatus ?? null,
        fundingMethod: item.fundingMethod ?? null,
        fundingStatus: item.fundingStatus ?? null,
        amount: amountFormatter(item.sendAmount ?? 0, item.sendCurrency),
        currency: item.sendCurrency,
        date: formatDate(item.createdAt),
        recipientName: recipientLabel(item.recipientHint),
        onSelect: item.originationId
          ? () =>
              redirectTo(
                `${APP_ROUTES.RECEIPT_PAGE}/${item.originationId}`
              )
          : undefined,
        onPayAtStore:
          canPay && item.operationId
            ? () => void handlePayAtStore(item.operationId!)
            : undefined,
        payingAtStore:
          launchingCashDeposit && payingOperationId === item.operationId,
      };
    },
    [redirectTo, handlePayAtStore, launchingCashDeposit, payingOperationId]
  );

  const transactions = useMemo<ITransaction[]>(
    () => originations.map(mapOrigination),
    [originations, mapOrigination]
  );

  const loadOriginations = useCallback(async () => {
    if (!linkId || loadingRef.current) return;

    loadingRef.current = true;
    try {
      const items = await listOriginations(linkId);
      setOriginations(items);
    } catch (error) {
      console.error("[HomePage] Failed to fetch originations:", error);
    } finally {
      loadingRef.current = false;
    }
  }, [linkId]);

  const refresh = useCallback(async () => {
    await loadOriginations();
  }, [loadOriginations]);

  const handleOriginationUpsert = useCallback((item: OriginationSummary) => {
    setOriginations((prev) => upsertOriginationInList(prev, item));
  }, []);

  useOriginationSSE(linkId, originations, handleOriginationUpsert, refresh);

  const watchSenderOperations = useMemo(
    () =>
      originations.some(
        (item) =>
          item.operationId &&
          !isOriginationComplete(item.bindingState, item.operationStatus)
      ) || hasNonTerminalOriginations(originations),
    [originations]
  );

  useSenderOperationRefresh(linkId, watchSenderOperations, refresh);

  const { pullDistance, refreshing, pulling } = usePullToRefresh(
    containerRef,
    refresh,
    { threshold: PULL_THRESHOLD }
  );

  useEffect(() => {
    if (linkId) {
      void loadOriginations();
    }
  }, [linkId, loadOriginations]);

  const { openSupport, supportSheet } = useSupportSheet();
  const inbox = useNovuInbox();
  const headerSlot = inbox?.headerSlot ?? "help";
  const showBell = headerSlot === "bell";
  const showHelp = headerSlot === "help";

  return (
    <>
      <AuraLayout
        colorConfig={colorConfig}
        className={
          showBell
            ? "need-help-header notifications-home-header"
            : "need-help-header"
        }
      >
        <Layout.Header>
          {headerSlot === "pending" ? null : (
            <Navbar
              title={showHelp ? t("common.needHelp") : undefined}
              align="right"
              onBackClick={() => redirectTo(APP_ROUTES.LANDING_PAGE)}
              onIconClick={showHelp ? () => openSupport() : undefined}
              icon={
                showBell ? (
                  <NotificationsBell />
                ) : (
                  <SupportIcon size={35} colors={dropzoneColors} />
                )
              }
            />
          )}
        </Layout.Header>
        <Layout.Content className="transactions-section">
          <TransactionsHistory
            items={transactions}
            containerRef={containerRef}
            pullDistance={pullDistance}
            refreshing={refreshing}
            pulling={pulling}
            pullThreshold={PULL_THRESHOLD}
          />
        </Layout.Content>
        <Layout.Footer>
          <section className="footer-section transactions-footer">
            {!canOriginate && (
              <BodyCopy
                textColor={bodyCopy}
                className="transactions-disabled-note"
              >
                {t("transactions.transfersDisabled")}
              </BodyCopy>
            )}
            {canExitToPartner && (
              <Button
                size="small"
                variant="cancel"
                background={backgroundBtn}
                color={backgroundBtn}
                className="alreary-acc-btn"
                onClick={exitToPartnerOrLanding}
              >
                {t("transactions.backHome")}
              </Button>
            )}
            <Button
              size="large"
              background={backgroundBtn}
              color={textColorBtn}
              disabled={!canOriginate}
              onClick={startNewTransfer}
            >
              {t("transactions.newTransfer")}
            </Button>
          </section>
        </Layout.Footer>
      </AuraLayout>
      {supportSheet}
    </>
  );
});
